import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const functionsUrl = typeof __DEV__ !== 'undefined' && __DEV__
  ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
  : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

const STATUS_COLORS = { fresh: '#34C759', warm: '#FF9500', stale: '#FF3B30' };

function ageLabel(timestampMs) {
  if (!timestampMs) return { text: 'unknown', color: STATUS_COLORS.stale };
  const ageSec = (Date.now() - timestampMs) / 1000;
  if (ageSec < 600) return { text: `${Math.round(ageSec)}s ago`, color: STATUS_COLORS.fresh };
  if (ageSec < 1200) return { text: `${Math.round(ageSec / 60)}m ago`, color: STATUS_COLORS.warm };
  return { text: `${Math.round(ageSec / 60)}m ago`, color: STATUS_COLORS.stale };
}

export default function BestBetsCacheTab() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'best_bets_cache'), orderBy('timestamp', 'desc')));
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDocs(items);
    } catch (err) {
      console.error('BestBetsCacheTab fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      await fetch(`${functionsUrl}/refreshBestBetsEndpoint`, { method: 'POST' });
      setTimeout(fetchDocs, 3000);
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const leagueDocs = docs.filter(d => !d.type);
  const teamDocs = docs.filter(d => d.type === 'team');
  const fixtureDocs = docs.filter(d => d.type === 'fixture');

  const leagues = [...new Set(docs.map(d => d.league).filter(Boolean))].sort();
  const filtered = filter === 'all' ? docs : docs.filter(d => d.league === filter);
  const filteredLeague = filter === 'all' ? leagueDocs : leagueDocs.filter(d => d.league === filter);
  const filteredTeam = filter === 'all' ? teamDocs : teamDocs.filter(d => d.league === filter);
  const filteredFixture = filter === 'all' ? fixtureDocs : fixtureDocs.filter(d => d.league === filter);

  const totalBets = filtered.reduce((s, d) => s + (d.count || 0), 0);
  const freshCount = filtered.filter(d => Date.now() - (d.timestamp || 0) < 600000).length;
  const staleCount = filtered.filter(d => Date.now() - (d.timestamp || 0) > 1200000).length;

  if (loading) {
    return <View style={s.center}><ActivityIndicator size="large" color="#007AFF" /><Text style={s.loadingText}>Loading cache data...</Text></View>;
  }

  return (
    <ScrollView style={s.container} contentContainerStyle={s.content}>
      {/* Header */}
      <View style={s.headerRow}>
        <Text style={s.title}>Best Bets Cache</Text>
        <TouchableOpacity style={s.refreshBtn} onPress={triggerRefresh} disabled={refreshing}>
          {refreshing ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="refresh" size={16} color="#fff" />}
          <Text style={s.refreshText}>{refreshing ? 'Refreshing...' : 'Refresh Now'}</Text>
        </TouchableOpacity>
      </View>

      {/* Summary cards */}
      <View style={s.summaryRow}>
        <View style={[s.card, { borderLeftColor: '#007AFF' }]}>
          <Text style={s.cardNum}>{docs.length}</Text>
          <Text style={s.cardLabel}>Cache Docs</Text>
        </View>
        <View style={[s.card, { borderLeftColor: '#34C759' }]}>
          <Text style={s.cardNum}>{totalBets.toLocaleString()}</Text>
          <Text style={s.cardLabel}>Total Bets</Text>
        </View>
        <View style={[s.card, { borderLeftColor: STATUS_COLORS.fresh }]}>
          <Text style={s.cardNum}>{freshCount}</Text>
          <Text style={s.cardLabel}>Fresh (&lt;10m)</Text>
        </View>
        <View style={[s.card, { borderLeftColor: STATUS_COLORS.stale }]}>
          <Text style={s.cardNum}>{staleCount}</Text>
          <Text style={s.cardLabel}>Stale (&gt;20m)</Text>
        </View>
      </View>

      {/* League filter pills */}
      <View style={s.filterRow}>
        <TouchableOpacity style={[s.pill, filter === 'all' && s.pillActive]} onPress={() => setFilter('all')}>
          <Text style={[s.pillText, filter === 'all' && s.pillTextActive]}>All</Text>
        </TouchableOpacity>
        {leagues.map(l => (
          <TouchableOpacity key={l} style={[s.pill, filter === l && s.pillActive]} onPress={() => setFilter(l)}>
            <Text style={[s.pillText, filter === l && s.pillTextActive]}>{l.toUpperCase()}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* League-level cache */}
      <Text style={s.sectionTitle}>League Cache ({filteredLeague.length})</Text>
      {filteredLeague.map(d => {
        const age = ageLabel(d.timestamp);
        return (
          <View key={d.id} style={s.row}>
            <View style={[s.dot, { backgroundColor: age.color }]} />
            <Text style={s.rowId}>{d.id}</Text>
            <Text style={s.rowCount}>{d.count || 0} bets</Text>
            <Text style={[s.rowAge, { color: age.color }]}>{age.text}</Text>
          </View>
        );
      })}

      {/* Team-level cache */}
      {filteredTeam.length > 0 && (
        <>
          <Text style={s.sectionTitle}>Team Cache ({filteredTeam.length})</Text>
          {filteredTeam.slice(0, 50).map(d => {
            const age = ageLabel(d.timestamp);
            return (
              <View key={d.id} style={s.row}>
                <View style={[s.dot, { backgroundColor: age.color }]} />
                <Text style={s.rowId}>{d.team || d.id}</Text>
                <Text style={s.rowCount}>{d.count || 0} bets</Text>
                <Text style={[s.rowAge, { color: age.color }]}>{age.text}</Text>
              </View>
            );
          })}
          {filteredTeam.length > 50 && <Text style={s.moreText}>...and {filteredTeam.length - 50} more</Text>}
        </>
      )}

      {/* Fixture-level cache */}
      {filteredFixture.length > 0 && (
        <>
          <Text style={s.sectionTitle}>Fixture Cache ({filteredFixture.length})</Text>
          {filteredFixture.slice(0, 30).map(d => {
            const age = ageLabel(d.timestamp);
            return (
              <View key={d.id} style={s.row}>
                <View style={[s.dot, { backgroundColor: age.color }]} />
                <Text style={s.rowId}>{d.home_team || '?'} vs {d.away_team || '?'}</Text>
                <Text style={s.rowCount}>{d.count || 0} bets</Text>
                <Text style={[s.rowAge, { color: age.color }]}>{age.text}</Text>
              </View>
            );
          })}
          {filteredFixture.length > 30 && <Text style={s.moreText}>...and {filteredFixture.length - 30} more</Text>}
        </>
      )}
    </ScrollView>
  );
}

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0a0a0a' },
  content: { padding: 16, paddingBottom: 60 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 80 },
  loadingText: { color: '#888', marginTop: 12, fontSize: 14 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { color: '#fff', fontSize: 20, fontWeight: '700' },
  refreshBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#007AFF', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, gap: 6 },
  refreshText: { color: '#fff', fontSize: 13, fontWeight: '600' },
  summaryRow: { flexDirection: 'row', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  card: { flex: 1, minWidth: 100, backgroundColor: '#1a1a1a', borderRadius: 10, padding: 12, borderLeftWidth: 3 },
  cardNum: { color: '#fff', fontSize: 22, fontWeight: '700' },
  cardLabel: { color: '#888', fontSize: 11, marginTop: 2 },
  filterRow: { flexDirection: 'row', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
  pill: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#333' },
  pillActive: { backgroundColor: '#007AFF', borderColor: '#007AFF' },
  pillText: { color: '#888', fontSize: 12, fontWeight: '600' },
  pillTextActive: { color: '#fff' },
  sectionTitle: { color: '#ccc', fontSize: 15, fontWeight: '700', marginTop: 16, marginBottom: 8 },
  row: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#141414', padding: 10, borderRadius: 8, marginBottom: 4, gap: 8 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  rowId: { color: '#ddd', fontSize: 13, flex: 1, fontWeight: '500' },
  rowCount: { color: '#aaa', fontSize: 12 },
  rowAge: { fontSize: 12, fontWeight: '600', minWidth: 70, textAlign: 'right' },
  moreText: { color: '#666', fontSize: 12, textAlign: 'center', paddingVertical: 8 },
});
